import * as mobx from 'mobx';
import {
  defaultPollPropreties,
  PollChoiceStatus,
  TPollStatus,
  PollsTypes,
  PollsViewStatuses,
  TPollType,
  PollCommands,
  PollResultsSlideContent
} from '../../constans/polls-constants';
import ResponseEntity, { IResponseItem } from './response-entity';
import { v4 as uuidv4 } from 'uuid';
import { SHORT_PLAIN_TEXT_LENGTH } from '../../constans/common-constants';
import { calculatePollPercentages, sortByOrder } from '../../utils/helpers/polls-helper';
import { IPollSlideGenerateRequest } from '../../api/dao/polls-dao';

export interface IVoteCount {
  count: number;
  pollChoiceId: string;
}
export interface IPollEntity {
  status: PollsViewStatuses | TPollStatus;
  type: PollsTypes | TPollType;
  value: string;
  hideForPresenter: boolean;
  isSlideAdded?: boolean;
  countVotes?: number;
  pollId?: string;
  prioritizeIndex?: number;
  order?: number;
  responses?: ResponseEntity[];
  command?: string;
  publicAdjustment: number;
  pollStatusTimestamp?: string;
  poll_completed_timestamp?: string;
  result_slide_added: boolean;
  resultsShared?: boolean;
}

export interface IPollVotesResponse {
  poll_response: string;
  poll_vote_percentage: number;
  poll_id: string;
  vote_order: number;
}

class PollEntity implements IPollEntity {
  public pollId: string;
  public status: PollsViewStatuses;
  public type: PollsTypes;
  public value: string;
  public countVotes: number;
  public responses: ResponseEntity[];
  public prioritizeIndex: number;
  public hideForPresenter: boolean;
  public isSlideAdded: boolean;
  public snapshot: IPollEntity;
  public command: string;
  public publicAdjustment: number;
  public pollStatusTimestamp?: string;
  public result_slide_added: boolean;
  public resultsShared: boolean;
  public shouldShowGenerating:boolean;

  constructor (fromDTO: IPollEntity = defaultPollPropreties) {
    mobx.makeAutoObservable(this);
    this.deserialize(fromDTO);
    this.snapshot = { ...fromDTO };
    this.updateIsShowGenerating(false);
  }

  public getId = (): string => {
    return this.pollId;
  };

  public updateIsShowGenerating = (value: boolean): void => {
    this.shouldShowGenerating = value;
  };

  public setResponses = (responses: ResponseEntity[]): void => {
    this.responses = responses;
  };

  public addResponse = (response: ResponseEntity): void => {
    this.responses.push(response);
  };

  public deleteResponseById = (id: string): void => {
    this.responses = this.responses.filter(x => x.pollChoiceId !== id);
  };

  public findResponseById = (id: string): ResponseEntity | null => {
    return this.responses.find(x => x.pollChoiceId === id) || null;
  };

  public getFilteredResponses = (): ResponseEntity[] => {
    return this.responses.filter((el: ResponseEntity) => el.status !== PollChoiceStatus.DELETED);
  };

  // Keeps the "N Votes" header in sync with the vote-share percentages shown below it, which are
  // based on the same filtered (non-deleted) responses. Falls back to the server-provided
  // countVotes before responses have been fetched, so the header doesn't flash "0 Votes".
  public getVisibleVotesCount = (): number => {
    const filteredResponses = this.getFilteredResponses();
    if (!filteredResponses.length) return this.countVotes;

    return filteredResponses.reduce((sum, response) => sum + (response.count || 0), 0);
  };

  public setQuestion = (question: string): void => {
    this.value = question;
  };

  public isPollValueValid = (): boolean => {
    return !!this.value.trim().length && this.value.length <= SHORT_PLAIN_TEXT_LENGTH;
  };

  public getNextPollStatus = (): PollsViewStatuses => {
    switch (this.status) {
      case PollsViewStatuses.NOT_STARTED:
        return PollsViewStatuses.IN_PROGRESS;
      case PollsViewStatuses.IN_PROGRESS:
        this.command = PollCommands.START_POLL;
        return PollsViewStatuses.COMPLETED;
      case PollsViewStatuses.COMPLETED:
        this.command = PollCommands.STOP_POLL;
        return PollsViewStatuses.NOT_STARTED;
      default:
        return PollsViewStatuses.NOT_STARTED;
    }
  };

  public getCommand = (status: PollsViewStatuses): PollCommands => {
    switch (status) {
      case PollsViewStatuses.IN_PROGRESS:
        return PollCommands.START_POLL;
      case PollsViewStatuses.COMPLETED:
        return PollCommands.STOP_POLL;
      default:
        return PollCommands.START_POLL;
    }
  };

  public setResponseVoteResults = (votedChoices: IVoteCount[]): void => {
    for (const response of this.responses) {
      const votedChoice = votedChoices.find((el: IVoteCount) => el.pollChoiceId === response.pollChoiceId);
      response.count = votedChoice?.count || 0;
    }
  };

  public resetChoicesCounts = (): void => {
    this.responses = this.responses.map((el: ResponseEntity) => {
      el.count = 0;
      return el;
    });
  };

  public setStatus = (value: PollsViewStatuses): void => {
    this.status = value;
  };

  public setResultSlideAdded = (value: boolean): void => {
    this.result_slide_added = value;
  };

  public setResultsShared = (value: boolean): void => {
    this.resultsShared = value;
  };

  public serialize = (): IPollEntity => {
    return {
      pollId: this.pollId,
      prioritizeIndex: this.prioritizeIndex,
      status: this.status,
      type: this.type,
      value: this.value,
      responses: [...this.responses],
      hideForPresenter: this.hideForPresenter,
      isSlideAdded: this.isSlideAdded,
      countVotes: this.countVotes,
      publicAdjustment: this.publicAdjustment,
      pollStatusTimestamp: this.pollStatusTimestamp,
      result_slide_added: this.result_slide_added
    };
  };

  public deserialize = (fromDTO: IPollEntity): void => {
    this.pollId = fromDTO.pollId || uuidv4();
    this.isSlideAdded = fromDTO.isSlideAdded || false;
    this.hideForPresenter = fromDTO.hideForPresenter;
    this.prioritizeIndex = fromDTO.order || fromDTO.prioritizeIndex || 0;
    this.value = fromDTO.value;
    this.responses = fromDTO.responses || this.snapshot?.responses || [];
    this.status = PollsViewStatuses[fromDTO.status as TPollStatus] || fromDTO.status;
    this.type = PollsTypes[fromDTO.type as TPollType] || fromDTO.type;
    // The poll-update broadcast the backend issues carries no `countVotes`, so it arrives as null here.
    // Keep the count this session already knows instead of zeroing it — a wiped count reads as "0 Votes"
    // and hides the Show Results button in every session that didn't make the change.
    this.countVotes = fromDTO.countVotes ?? this.countVotes ?? 0;
    this.publicAdjustment = fromDTO.publicAdjustment || 0;
    this.pollStatusTimestamp = fromDTO.poll_completed_timestamp || this.snapshot?.poll_completed_timestamp;
    this.result_slide_added = fromDTO.result_slide_added || false;
    // listPolls returns `resultsShared`, but the onUpdatePoll subscription payload does not, so fall back
    // to the current value when it's absent rather than letting a push revert an optimistic Show/Hide toggle.
    this.resultsShared = fromDTO.resultsShared ?? this.resultsShared ?? false;
  };

  public getPollResponsesList = (): IPollVotesResponse[] => {
    const sortedResponses = sortByOrder(this.responses);
    const percentages = calculatePollPercentages(sortedResponses.map((item: IResponseItem) => item.count || 0));

    return sortedResponses.map((item: IResponseItem, idx) => ({
      poll_id: this.pollId,
      vote_order: idx,
      poll_response: item.value,
      poll_vote_percentage: percentages[idx]
    }));
  };

  public getPollSlideGenerateData = (presentationName: string, presentation_id: number | null): IPollSlideGenerateRequest => {
    return {
      presentation_id,
      status: this.status.toUpperCase(),
      type: PollResultsSlideContent.TYPE_SLIDE,
      name: `${presentationName}_${this.value}`,
      poll_id: this.pollId,
      poll_title: this.value,
      poll_votes:  this.getPollResponsesList(),
    };
  };
}

export default PollEntity;
